import { isEmpty, isSchoolAdmin, getBooleanFilterDefaults, dedupeFreeTextFilterOptions, buildTopCountPartitionDomainList, DIRECTORY_TOP_COUNT_FILTER_KEYS, TOP_COMPANY_GROUP_LABEL, mapDomainElementsInCodeOrder, appendCustomCompanyFilterOptions, getDirectoryFilterCountsKey } from '../../../library/Core/SmartFunctions';
import { State } from '../../../library/Core/SmartTypes';
import { axiosClient } from '../../../library/Service/axiosClient';
import { SUBCATEGORY_QUESTION_MAP } from '../../admin/reports/markerSubCategoryMap';
import { STAGE_LABELS, STAGES } from '../../watchlists/report/labelMaps';

class DomainExpertDirectoryService {

    // ── filterConfig ─────────────────────────────────────────────────────────
    static filterConfig: any = {
    // ---------------- Primary Filters ----------------
      activationStatus: {
        id: 'activationStatus',
        label: 'Activation Status',
        domain: 'WATCHLIST_STAGE',
        domainSort: 'pipeline',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: false,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Status of engagement and contribution intent towards Thought Leadership outcome',
    },
    programId: {
        id: 'programId',
        label: 'Program Name',
        domain: 'ALL_PROGRAM_CODE',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: false,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Academic program of the domain expert alumnus.',
    },
    passOutYear: {
        id: 'passOutYear',
        label: 'Batch Year',
        domain: 'PAST_YEAR_CODE',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: false,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Year of graduation of the domain expert.',
    },
    domainExpertiseArea: {
        id: 'domainExpertiseArea',
        label: 'Expertise',
        domain: null,
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: false,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Area of expertise of the domain expert alumnus.',
    },
    industryId: {
        id: 'industryId',
        label: 'Industry',
        domain: 'INDUSTRY_CODE',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: false,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Industry sector in which the domain expert currently works.',
    },
    jobCompanyId: {
        id: 'jobCompanyId',
        label: 'Organization',
        domain: 'COMPANY_CODE',
        domainGroup: 'topPartition',
        topPartitionGroupLabel: TOP_COMPANY_GROUP_LABEL,
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: false,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Current organization of the domain expert.',
    },
    seniorityLevelCode: {
        id: 'seniorityLevelCode',
        label: 'Seniority Level',
        domain: 'JOB_SENIORITY_LEVEL',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: false,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Professional seniority level of the domain expert.',
    },
    functionalAreaCode: {
        id: 'functionalAreaCode',
        label: 'Functional Area',
        domain: 'FUNCTIONAL_AREA_CODE',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: false,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Functional area of expertise of the domain expert.',
    },

    // ---------------- Additional Filters ----------------
    workingStatusCode: {
        id: 'workingStatusCode',
        label: 'Working Status',
        domain: 'WORK_STATUS_CODE',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: true,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Current working status of the domain expert.',
    },
    careerTrack: {
        id: 'careerTrack',
        label: 'Career Track',
        domain: 'CAREER_TRACK',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: true,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Career track of the domain expert alumnus.',
    },
    countryCode: {
        id: 'countryCode',
        label: 'Country',
        domain: 'COUNTRY_CODE',
        domainGroup: 'topPartition',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: true,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Current country of residence of the domain expert.',
    },
    cityCode: {
        id: 'cityCode',
        label: 'City',
        domain: 'CITY_CODE',
        domainGroup: 'topPartition',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: true,
        isVisible: true,
        hasSearch: true,
        parentIdApply: true,
        parentId: 'countryCode',
        tooltip: 'Current city of residence of the domain expert.',
    },
    activeMarkers: {
        id: 'activeMarkers',
        label: 'Active Markers',
        domain: 'MARKER_CODE',
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: true,
        isVisible: true,
        hasSearch: true,
        tooltip: 'Involvement and affinity markers activated on the domain expert profile.',
    },
    almaMatePride: {
        id: 'almaMatePride',
        label: 'Alma Mater Pride',
        domain: null,
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: true,
        isVisible: false,
        hasSearch: false,
        isBurner: true,
        tooltip: 'Degree of alumni pride in their alma mater (from Burner Analysis).',
    },
    marketabilityQuotient: {
        id: 'marketabilityQuotient',
        label: 'Marketability Quotient',
        domain: null,
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: true,
        isVisible: false,
        hasSearch: false,
        isBurner: true,
        tooltip: 'Alumni marketability score from Burner Analysis.',
    },
    vibrancyLevel: {
        id: 'vibrancyLevel',
        label: 'Vibrancy Level',
        domain: null,
        applicableFor: 'BOTH',
        class: 'fiterItem width-25 mb-3',
        isAdditional: true,
        isVisible: false,
        hasSearch: false,
        isBurner: true,
        tooltip: 'Alumni network vibrancy score from Burner Analysis.',
    },
    isPrestigiousCollege: {
        id: 'isPrestigiousCollege',
        label: 'Prestigious Colleges',
        filterType: 'boolean',
        isShowProfileFilter: true,
        displayOrder: 1,
        booleanCheckedValue: true,
        applicableFor: 'BOTH',
        isVisible: false,
    },
    isFortune500: {
        id: 'isFortune500',
        label: 'Fortune500',
        filterType: 'boolean',
        isShowProfileFilter: true,
        displayOrder: 2,
        booleanCheckedValue: true,
        applicableFor: 'BOTH',
        isVisible: false,
    },
    isMNC: {
        id: 'isMNC',
        label: 'MNCs',
        filterType: 'boolean',
        isShowProfileFilter: true,
        displayOrder: 3,
        booleanCheckedValue: true,
        applicableFor: 'BOTH',
        isVisible: false,
    },
    isStartup: {
        id: 'isStartup',
        label: 'Startups',
        filterType: 'boolean',
        isShowProfileFilter: true,
        displayOrder: 4,
        booleanCheckedValue: true,
        applicableFor: 'BOTH',
        isVisible: false,
    },
    isUnicorn: {
        id: 'isUnicorn',
        label: 'Unicorns',
        filterType: 'boolean',
        isShowProfileFilter: true,
        displayOrder: 5,
        booleanCheckedValue: true,
        applicableFor: 'BOTH',
        isVisible: false,
    },
    isNGO: {
        id: 'isNGO',
        label: 'NGOs',
        filterType: 'boolean',
        isShowProfileFilter: true,
        displayOrder: 6,
        booleanCheckedValue: true,
        applicableFor: 'BOTH',
        isVisible: false,
    },
    isGovernment: {
        id: 'isGovernment',
        label: 'Government',
        filterType: 'boolean',
        isShowProfileFilter: true,
        displayOrder: 7,
        booleanCheckedValue: true,
        applicableFor: 'BOTH',
        isVisible: false,
    },
};

    // ── Page Loader ───────────────────────────────────────────────────────────
    static pageLoader(params: any) {
        const state: any = {};

        return Promise.all([
            DomainExpertDirectoryService.getFilterDomain(),
            DomainExpertDirectoryService.getSummaryCounts('MY_SCHOOL'),
        ]).then(([filtersRes, summaryRes]) => {
            const filterData = filtersRes.data || {};
            const enabledOutcomes: string[] = filterData.enabledOutcomes || [];
            const hasBurnerAnalysis: boolean = !!filterData.hasBurnerAnalysis;

            // Inject Burner Analysis stages visibility
            Object.keys(DomainExpertDirectoryService.filterConfig).forEach((key) => {
                const cfg = DomainExpertDirectoryService.filterConfig[key];
                if (cfg.isBurner) cfg.isVisible = hasBurnerAnalysis;
            });

            state.data = {
                searchLevel: 'MY_SCHOOL',
                students: [],
                selectedCard: 'totalDomainExperts',
                summary: summaryRes.data,
                summaryCountWithoutFilter: summaryRes.data,
                enabledOutcomes,
                hasBurnerAnalysis,
            };
            state.internal = {
                searchFilterDomains: filterData,
                enabledOutcomes,
                hasBurnerAnalysis,
            };
            return Promise.resolve(state);
        });
    }

    // ── API Calls ─────────────────────────────────────────────────────────────
    static getFilterDomain() {
        return axiosClient().get(
            `${process.env.REACT_APP_STUDENT_MANAGEMENT_SERVER}/domain-expert-directory/management-filters`
        );
    }

    // Returns the institute ID array for the current user — uses user.institutes for
    // school admins (same pattern as RecruiterDirectoryService line 98).
    static getSchoolIds(): string[] {
        const user = JSON.parse(localStorage.getItem('user') as string);
        return isSchoolAdmin() ? (user.institutes || []) : [user.instituteId].filter(Boolean);
    }

    static getSummaryCounts(level: string = 'MY_SCHOOL') {
        const schoolIds = DomainExpertDirectoryService.getSchoolIds();
        return axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/domain-expert-directory/summary-counts`,
            { schoolId: schoolIds, level }
        );
    }

    // Strip false/null boolean filters before sending — only include when true.
    // Array filters stay as-is (empty [] = not applied).
    static cleanCriteria(criteria: any): any {
        const cleaned: any = {};
        for (const [key, val] of Object.entries(criteria)) {
            if (val === false || val === null || val === undefined) continue;
            cleaned[key] = val;
        }
        return cleaned;
    }

    static async performSearchWithUserSelectedCriteria(
        searchCriteria: any,
        state: State,
        dispatch: any,
        newState: any = {}
    ) {
        const schoolIds = DomainExpertDirectoryService.getSchoolIds();

        const defaultCriteria = DomainExpertDirectoryService.getDefaultSearchCriteria();
        const criteria = DomainExpertDirectoryService.cleanCriteria({
            ...defaultCriteria,
            ...searchCriteria,
            schoolId: schoolIds,
            context: {
                ...defaultCriteria.context,
                ...searchCriteria?.context,
                level: 'MY_SCHOOL',
                page: 1,
                pageSize: 20,
            },
        });

        // summary-counts is NOT re-called here — it's loaded once by pageLoader().
        // Summary card counts are institute-wide absolute numbers that don't change
        // when search filters are applied, so no need to refresh them on every search.
        const searchRes = await axiosClient().post(
            `${process.env.REACT_APP_REPORTS_SERVER}/domain-expert-directory/search`,
            criteria
        );

        dispatch({
            type: 'REFRESH_DATA',
            payload: {
                data: {
                    ...(state as any).data,
                    ...newState,
                    // students: searchRes.data.students || [],
                    students: DomainExpertDirectoryService.dedupeByUuid(searchRes.data.students || []),
                    totalCount: searchRes.data.totalCount ?? 0,
                    filters: searchRes.data.filters || criteria,
                    selectedCard: searchCriteria?.context?.selectedCard ?? (state as any).data.selectedCard,
                },
            },
        });
    }

        // A school-admin can manage more than one institute in the same group, and the backend's
    // activation join (agg subquery) groups by (student_id, institute_id) — so an alumnus with an
    // activation snapshot row under 2 institute_ids in scope comes back as 2 rows. Both rows have
    // the same uuid (and usually the same program_name), so they render as visually identical,
    // "repeated" cards. Collapse to one row per alumnus, keeping the first occurrence.
    static dedupeByUuid(students: any[]): any[] {
        const seen = new Set<string>();
        return students.filter((s) => {
            if (!s?.uuid || seen.has(s.uuid)) return false;
            seen.add(s.uuid);
            return true;
        });
    }

    // ── Criteria helpers ──────────────────────────────────────────────────────
    static getDefaultSearchCriteria() {
        return {
            name: '',
            activationStatus: [],
            programId: [],
            domainExpertiseArea: [],
            passOutYear: [],
            industryId: [],
            jobCompanyId: [],
            seniorityLevelCode: [],
            functionalAreaCode: [],
            workingStatusCode: [],
            careerTrack: [],
            countryCode: [],
            cityCode: [],
            activeMarkers: [],
            almaMatePride: [],
            marketabilityQuotient: [],
            vibrancyLevel: [],
            ...getBooleanFilterDefaults(DomainExpertDirectoryService.filterConfig),
            context: { level: 'MY_SCHOOL', page: 1, pageSize: 20 },
        };
    }

    static getCriteriaForSelectedCard(selectedCard: string, state: State) {
        const schoolIds = DomainExpertDirectoryService.getSchoolIds();
        const data = (state as any).data;

        const criteriaLookup: any = {
            totalDomainExperts:     { schoolId: schoolIds },
            answeredQueries:        { schoolId: schoolIds, hasAnsweredQuery: true },
            contributedWebinars:    { schoolId: schoolIds, hasContributedWebinar: true },
            conductedGuestLectures: { schoolId: schoolIds, hasConductedGuestLecture: true },
            maxPotentialBatch: {
                schoolId: schoolIds,
                passOutYear: [String(data?.summary?.maxPotentialBatch)],
                programId:   data?.summary?.maxPotentialProgramId
                    ? [String(data.summary.maxPotentialProgramId)]
                    : [],
            },
        };

        return criteriaLookup[selectedCard] || { schoolId: schoolIds };
    }

    static getActiveMarkerDomain(markerCodes: string[] = []) {

    if (!Array.isArray(markerCodes) || markerCodes.length === 0) {
        return [];
    }

    // Only these categories should appear
    const markerOptions = [
        ...(SUBCATEGORY_QUESTION_MAP.affinity || []),
        ...(SUBCATEGORY_QUESTION_MAP.general || []),
        ...(SUBCATEGORY_QUESTION_MAP.digitalCampaign || [])
    ];

    return markerOptions
        .filter(option => markerCodes.includes(option.code))
        .sort((a, b) => a.value.localeCompare(b.value));
}

    // ── Domain mapping ────────────────────────────────────────────────────────
    static getSearchFilterDomains(domain: any, customDomains: any) {
        const user = JSON.parse(localStorage.getItem('user') as string);
        const { passOutYear } = user;

        const getDomainForCodes = (domainMap: any, categoryCode: string, codes: any) => {
            if (isEmpty(codes) || !Array.isArray(codes)) return [];
            const stringCodes = codes.map((c: any) => String(c));

            let targetDomain = domainMap
                ?.get(categoryCode)
                ?.filter((d: any) => stringCodes.includes(String(d.code)));
            if (!targetDomain) return [];

            if (categoryCode === 'PAST_YEAR_CODE') {
                targetDomain = targetDomain.map((d: any) =>
                    String(d.code) === String(passOutYear)
                        ? { ...d, value: `${d.value} (your batch)` }
                        : d
                );
                return targetDomain.sort((a: any, b: any) =>
                    b.value.toLowerCase().localeCompare(a.value.toLowerCase())
                );
            }
            return targetDomain.sort((a: any, b: any) =>
                a.value.toLowerCase().localeCompare(b.value.toLowerCase())
            );
        };

        const searchFilterDomains: any = {};
        if (customDomains) {
            Object.keys(customDomains).forEach((key: any) => {

                // Active Markers are not part of Domain table
                if (key === "activeMarkers") {
                    searchFilterDomains.activeMarkers =
                        DomainExpertDirectoryService.getActiveMarkerDomain(
                            customDomains.activeMarkers
                        );
                    return;
                }

                // Expertise tags are free text (split from domain_expertise_area server-side),
                // not a coded domain — use the tag itself as both code and label.
                if (key === "domainExpertiseArea") {
                    searchFilterDomains.domainExpertiseArea = dedupeFreeTextFilterOptions(
                        customDomains.domainExpertiseArea || []
                    );
                    return;
                }

                // Meta / non-checkbox keys from management-filters API — not domain filter lists
                if (key === 'activationStages' || key === 'totalRecords' || key === 'hasBurnerAnalysis') {
                    return;
                }

                const cfg = DomainExpertDirectoryService.filterConfig[key];

                if (!cfg) {
                    return;
                }

                if (DIRECTORY_TOP_COUNT_FILTER_KEYS.has(key)) {
                    const counts = customDomains[getDirectoryFilterCountsKey(key)] ?? null;
                    const mapped = mapDomainElementsInCodeOrder(domain, cfg.domain, customDomains[key]);
                    const withCustom = key === 'jobCompanyId'
                        ? appendCustomCompanyFilterOptions(mapped, customDomains[key])
                        : mapped;
                    searchFilterDomains[key] = buildTopCountPartitionDomainList(
                        withCustom,
                        customDomains[key],
                        10,
                        counts
                    );
                } else {
                    searchFilterDomains[key] = getDomainForCodes(domain, cfg.domain, customDomains[key]);
                }
            });
        }

        // Static options for Burner Analysis filters
        const burnerOptions = [
            { code: 'HIGH',     value: 'High' },
            { code: 'MODERATE', value: 'Moderate' },
            { code: 'LOW',      value: 'Low' },
        ];
        searchFilterDomains.almaMatePride = burnerOptions;
        searchFilterDomains.marketabilityQuotient = burnerOptions;
        searchFilterDomains.vibrancyLevel = burnerOptions;

        // Activation Status — pipeline order (Dormant & Denied last), labels match watchlist layer
        const activationStages: string[] = customDomains?.activationStages || [];
        searchFilterDomains.activationStatus = STAGES.filter((stage) => activationStages.includes(stage)).map(
            (stage) => ({ code: stage, value: STAGE_LABELS[stage] || stage })
        );

        return searchFilterDomains;
    }
}

export default DomainExpertDirectoryService;
